// Workflow Cache · per-tenant workflow + states + transitions
// see docs/WORKFLOW_DESIGN_v0.1.md § Frontend State-Machine Architecture
// Phase 2c (2026-04-23)
//
// 策略：
//   1. mount 時先讀 Dexie（同步快 < 10ms），不阻塞首屏
//   2. 背景 fetch；server 用 workflow.version 當 ETag
//   3. 304 不動；200 更新 Dexie + 廣播 `workflow-updated` event 讓 component re-render
//   4. 無網路時靜默失敗（UI 仍以 cache 渲染，訂單流程可離線操作）

import { db } from './db.js';
import { fetchWorkflow } from './api.js';

const EVENT_UPDATED = 'workflow-updated';

/**
 * 取得 tenant 的 workflow（含 states + transitions）。
 * @param {string} tenantId
 * @returns {Promise<Workflow>} workflow meta + states + transitions
 */
export async function getWorkflow(tenantId) {
  if (!tenantId) throw new Error('getWorkflow: tenantId required');

  const cached = await db.workflow_cache.get(tenantId);
  if (cached) {
    // Background refresh（不 await，不阻塞）
    refreshInBackground(tenantId, cached.version);
    return cached.data;
  }

  // Cold path：首次 fetch
  return fetchAndCache(tenantId);
}

async function fetchAndCache(tenantId) {
  const data = await fetchWorkflow({ tenantId });
  await db.workflow_cache.put({
    id: tenantId,
    version: data.version,
    data,
    cachedAt: Date.now(),
  });
  return data;
}

async function refreshInBackground(tenantId, knownVersion) {
  try {
    const data = await fetchWorkflow({ tenantId, ifNoneMatch: knownVersion });
    if (!data || data.__not_modified) return;  // 304
    await db.workflow_cache.put({
      id: tenantId,
      version: data.version,
      data,
      cachedAt: Date.now(),
    });
    window.dispatchEvent(new CustomEvent(EVENT_UPDATED, { detail: data }));
  } catch (_err) {
    // 離線 / 網路錯誤：靜默。UI 仍以 cache 渲染。
  }
}

/**
 * 把 workflow 展開為 Map 結構供 component 快速查詢：
 *   stateMap: code → state row（含 label/color/flags）
 *   transitionsFrom: from_code → [{ to_code, label, allowed_roles }]
 */
export function buildWorkflowMaps(workflow) {
  const stateMap = new Map();
  const stateById = new Map();
  for (const s of workflow.states || []) {
    stateMap.set(s.code, s);
    stateById.set(s.id, s);
  }

  const transitionsFrom = new Map();
  for (const t of workflow.transitions || []) {
    const fromState = stateById.get(t.from_state);
    const toState = stateById.get(t.to_state);
    if (!fromState || !toState) continue;  // 防 stale cache
    const arr = transitionsFrom.get(fromState.code) || [];
    arr.push({
      to_code: toState.code,
      to_label: toState.label,
      to_color: toState.color,
      label: t.label || `→ ${toState.label}`,
      allowed_roles: t.allowed_roles || [],
      sort_order: t.sort_order || 0,
    });
    transitionsFrom.set(fromState.code, arr);
  }

  // 每組 transitions 依 sort_order 排
  for (const arr of transitionsFrom.values()) {
    arr.sort((a, b) => a.sort_order - b.sort_order);
  }

  return { stateMap, transitionsFrom };
}

/**
 * 動態取代 order-state-machine.js 的 allowedTransitionsForRole
 * @param {Workflow} workflow
 * @param {string} currentStateCode
 * @param {string} role
 * @returns {Array<{to_code, label, allowed_roles}>}
 */
export function allowedTransitionsFromWorkflow(workflow, currentStateCode, role) {
  if (!workflow || !currentStateCode || !role) return [];
  const { stateMap, transitionsFrom } = buildWorkflowMaps(workflow);
  const from = stateMap.get(currentStateCode);
  // 終態：no transitions out（即使 DB 層沒擋 transitions，這裡 defense-in-depth）
  if (from?.is_terminal_success || from?.is_terminal_cancel) return [];

  const candidates = transitionsFrom.get(currentStateCode) || [];
  return candidates.filter(t => t.allowed_roles.includes(role));
}

/**
 * 取得某 state code 的顯示資訊（label / color）
 * 用於 order 卡片 status badge 著色
 */
export function getStateDisplay(workflow, code) {
  if (!workflow) return { label: code, color: '#6B7280' };
  const { stateMap } = buildWorkflowMaps(workflow);
  const s = stateMap.get(code);
  if (!s) return { label: code, color: '#6B7280' };
  return { label: s.label, color: s.color };
}

export const WORKFLOW_EVENT_UPDATED = EVENT_UPDATED;
